/**
 * AdminLayout.jsx  —  v3
 *
 * Bilgi mimarisi: ust seviyedeki maddelerin TAMAMI birer GRUP (drawer).
 * Ayri bir "Dashboard" maddesi YOK — '/admin' dogrudan Danisman Ana
 * Sayfasi'ni acar (bkz. NAV_PATH_ALIASES) ve eski AdminDashboard sayfasi
 * menude yer almadan '/admin/genel-bakis' adresinde durur. Agacin kendisi
 * lib/adminNav.js icinde; burasi yalnizca render eder.
 *
 * Features:
 *   - Collapsible desktop sidebar (icon-only mode); daraltilmisken bir gruba
 *     tiklamak kenar cubugunu ACAR ve o grubu genisletir
 *   - Responsive: tablet/mobile uses shadcn Sheet drawer (KONTROLLU — bir
 *     yaprak linke tiklaninca cekmece kapanir)
 *   - Breadcrumb in top bar (derived from current route)
 *   - Cmd+K stub (opens empty modal — Sprint 5 will fill it)
 *   - Anomaly badge on "Suspicious Activity" sidebar link (polling 60 s)
 *   - i18n: all labels via t(labelKey) from LanguageContext
 *
 * NO global top bar (bkz. proje-yoneticisi görev talimatı, 2026-08-13 —
 * kullanıcı şikayeti, 17.png): eskiden bu sayfaların üstünde global Navbar
 * (logo + dil + bildirim zili + avatar) render ediliyordu (App.js'te
 * isAdminRoute yalnızca '/admin' önekini kapsıyordu, '/danisman' değil).
 * Artık '/danisman/*' de isAdminRoute'a dahil — global Navbar TÜM panel
 * sayfalarında (admin + danışman) gizli. Yerine:
 *   - Kullanıcı menüsü (avatar + ad + rol → Dil değiştir + Siteye Dön +
 *     Çıkış) sidebar'ın EN ALTINA taşındı (bkz. SidebarContent — "User
 *     footer" dropdown). Daraltılmış (icon-only) modda da çalışır.
 *   - Bildirim zili içerik alanının sağ üstüne taşındı, YALNIZCA
 *     admin/superadmin görür (danışman rolünde hiç render edilmez).
 */

import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  Menu,
  LogOut,
  ShieldCheck,
  ChevronDown,
  ExternalLink,
  Globe,
  ChevronUp,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from '../ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import NotificationBell from '../cabin/NotificationBell';
import { useOpenAnomalyCount } from '../../api/security';
// Menu agaci ve aktiflik / otomatik-acilma hesabi ayri bir saf modulde —
// bu dosya yalnizca RENDER eder (bkz. src/lib/adminNav.js).
import {
  navForRole,
  isLeafActive,
  isBranchActive,
  autoOpenGroupIds,
  normalizeNavPath,
} from '../../lib/adminNav';

// Kullanıcı menüsündeki rol etiketi — merkezi i18n sözlüğünde panel rolleri
// için ayrı anahtar yok (Navbar.js da aynı şekilde inline lang==='tr' deseni
// kullanıyor, bkz. user.role badge'i), o yüzden aynı desen tekrarlanıyor.
function roleLabel(role, lang) {
  const map = {
    admin: { tr: 'Yönetici', en: 'Admin' },
    superadmin: { tr: 'Süper Yönetici', en: 'Super Admin' },
    danisman: { tr: 'Danışman', en: 'Advisor' },
    instructor: { tr: 'Eğitmen', en: 'Instructor' },
  };
  const entry = map[role];
  if (!entry) return role || '';
  return lang === 'tr' ? entry.tr : entry.en;
}

// Breadcrumb / page-title helpers were removed in the "single-header" refactor.
// Each page now owns its own <PageHeader breadcrumbs={[...]} title={...} />.
// If a shared helper is needed later, move it to src/utils/adminBreadcrumbs.ts.

// ── Sidebar Nav Item ──────────────────────────────────────────────────────────

/**
 * NavItem — ozyinelemeli. Bir madde `children` tasiyorsa acilir/kapanir bir
 * drawer olarak render edilir ve cocuklarini YINE NavItem ile basar; boylece
 * ikinci, ucuncu... seviye kendiliginden desteklenir. (Eski surumde cocuk her
 * zaman <Link> olarak basiliyordu; `children` iceren bir cocuk sessizce
 * kayboluyordu.)
 *
 * `depth` yalnizca gorsel: 0 = ust seviye (18px ikon), >0 = ic seviye (16px).
 *
 * `navPathname` bilerek prop olarak geliyor, burada useLocation() cagrilmiyor:
 * aktiflik hesabi NORMALIZE edilmis adrese bakmali ('/admin' -> '/danisman',
 * bkz. normalizeNavPath) ve bu normalizasyon SidebarContent'te bir kez yapilir.
 */
function NavItem({
  item,
  collapsed,
  onNavigate,
  anomalyCount = 0,
  depth = 0,
  openIds,
  navPathname,
  onExpandSidebar,
}) {
  const { t } = useLanguage();
  const nested = depth > 0;

  // Bu grup kendiliginden acilmali mi? openIds agacin TAMAMINA bakilarak bir
  // kez hesaplanir (bkz. autoOpenGroupIds); burada sadece okunur. Bilerek
  // boolean'a indirgeniyor — openIds her render'da yeni bir Set oldugu icin
  // dogrudan bagimlilik olarak verilirse asagidaki effect her render kosar ve
  // kullanicinin ELIYLE kapattigi drawer'i tekrar acardi.
  const shouldAutoOpen = Boolean(item.children && openIds && openIds.has(item.id));

  // open is URL-driven: auto-open when current path is inside this group.
  // Manual toggle is also supported.
  const [open, setOpen] = useState(() => shouldAutoOpen);

  // Sync open state when route changes — always expand the active group.
  // Ozyineleme sayesinde ic drawer da dis drawer da ayni anda acilir
  // (torun aktifse dede de acilir).
  useEffect(() => {
    if (shouldAutoOpen) {
      setOpen(true);
    }
  }, [shouldAutoOpen]);

  const isActive = item.children ? isBranchActive(item, navPathname) : isLeafActive(item, navPathname);

  const Icon = item.icon;
  const label = t(item.labelKey);
  // Grup butonu ile actigi panel arasindaki ekran-okuyucu bagi. Menu iki
  // seviye derinlestigi icin bu bag olmadan kullanici bir maddenin acilir
  // grup mu yoksa yaprak link mi oldugunu duyamiyordu.
  const submenuId = `admin-nav-${item.id}-submenu`;
  const submenuOpen = !collapsed && open;
  const badge = item._badgeKey === 'anomaly' ? anomalyCount : 0;

  if (!item.children) {
    return (
      <Link
        to={item.path}
        onClick={onNavigate}
        className={`flex items-center gap-3 px-3 rounded-xl transition-all text-sm font-semibold group ${
          nested ? 'py-2' : 'py-2.5 mb-0.5'
        } ${
          isActive
            ? 'bg-[var(--brand-primary)] text-white shadow-sm'
            : `${nested ? 'text-white/50' : 'text-white/60'} hover:text-white hover:bg-white/8`
        } ${collapsed ? 'justify-center' : ''}`}
        title={collapsed ? label : undefined}
        data-testid={`admin-nav-${item.id}`}
      >
        <Icon size={nested ? 16 : 18} strokeWidth={2} className="flex-shrink-0" />
        {!collapsed && <span className="truncate flex-1">{label}</span>}
        {!collapsed && badge > 0 && (
          <span className="flex-shrink-0 w-4 h-4 bg-rose-500 text-white rounded-full text-[9px] font-bold flex items-center justify-center">
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </Link>
    );
  }

  return (
    <div>
      <button
        /* Daraltilmis modda cocuklar RENDER EDILMEZ; menunun yedi ust
           maddesinin yedisi de grup oldugu icin ("Genel Bakis" kalkti) eskiden
           burada tiklama hicbir sey yapmiyordu ve daraltilmis kenar cubugu
           fiilen kullanilamaz hale geliyordu. Artik tek tik kenar cubugunu
           ACAR ve bu grubu genisletir — tiklayan kullanici gezinmek istiyordur.
           (Ucar menu / flyout daha sik olurdu ama bu tur tarayicida
           dogrulanamadigi icin bilerek en dusuk riskli cozum secildi.) */
        onClick={() => {
          if (collapsed) {
            setOpen(true);
            onExpandSidebar && onExpandSidebar();
            return;
          }
          setOpen((o) => !o);
        }}
        className={`w-full flex items-center gap-3 px-3 rounded-xl mb-0.5 transition-all text-sm font-semibold ${
          nested ? 'py-2' : 'py-3'
        } ${
          isActive
            ? 'text-white bg-white/10'
            : `${nested ? 'text-white/50' : 'text-white/60'} hover:text-white hover:bg-white/8`
        } ${collapsed ? 'justify-center' : ''}`}
        title={collapsed ? label : undefined}
        data-testid={`admin-nav-${item.id}`}
        aria-expanded={submenuOpen}
        /* Panel yalnizca acikken render edilir; kapaliyken var olmayan bir
           id'yi isaret etmemek icin bilerek undefined birakiliyor. */
        aria-controls={submenuOpen ? submenuId : undefined}
      >
        <Icon size={nested ? 16 : 18} strokeWidth={2} className="flex-shrink-0" />
        {!collapsed && (
          <>
            <span className="truncate flex-1 text-left">{label}</span>
            <ChevronDown
              size={14}
              className={`flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
            />
          </>
        )}
      </button>

      <AnimatePresence initial={false}>
        {submenuOpen && (
          <motion.div
            id={submenuId}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <div className="pl-4 mb-1 space-y-0.5">
              {item.children.map((child) => (
                <NavItem
                  key={child.id}
                  item={child}
                  /* Cocuklar yalnizca `!collapsed` iken render edilir; yine de
                     bayragi oldugu gibi gecerek daraltilmis modda beklenmedik
                     bir sekilde acik kalmalarini engelliyoruz. */
                  collapsed={collapsed}
                  onNavigate={onNavigate}
                  anomalyCount={anomalyCount}
                  depth={depth + 1}
                  openIds={openIds}
                  navPathname={navPathname}
                  onExpandSidebar={onExpandSidebar}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Sidebar ───────────────────────────────────────────────────────────────────

function SidebarContent({ collapsed, setCollapsed, lang, mobile = false, onNavigate, anomalyCount = 0 }) {
  const { user, logout } = useAuth();
  const { toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleLogout = async () => {
    setUserMenuOpen(false);
    onNavigate && onNavigate();
    await logout();
    navigate('/');
  };

  const handleBackToSite = () => {
    setUserMenuOpen(false);
    onNavigate && onNavigate();
    navigate('/');
  };

  const handleToggleLanguage = () => {
    toggleLanguage();
    setUserMenuOpen(false);
  };

  // Kendiliginden acilacak gruplar agacin tamamina bakilarak burada BIR KEZ
  // hesaplanir; NavItem yalnizca okur. Masaustu ve mobil (Sheet) ayni
  // SidebarContent'i kullandigi icin iki yol da ayni hesaba baglidir.
  //
  // Adres once NORMALIZE edilir: '/admin' kok route'u Danisman Ana Sayfasi'ni
  // gosteriyor ama menude '/admin' path'li yaprak yok — normalizasyon olmadan
  // ayni ekran '/admin' ile acildiginda menude sifir vurgu / tum drawer'lar
  // kapali, '/danisman' ile acildiginda ise duzgun gorunurdu.
  const navItems = navForRole(user?.role);
  const navPathname = normalizeNavPath(location.pathname);
  const openIds = useMemo(
    () => autoOpenGroupIds(navItems, navPathname),
    [navItems, navPathname]
  );

  return (
    <div
      className={`flex flex-col h-full bg-[var(--bg-sidebar)] text-white transition-all duration-300 ${
        mobile ? 'w-72' : collapsed ? 'w-[68px]' : 'w-64'
      }`}
    >
      {/* Header */}
      <div
        className={`flex items-center ${
          collapsed && !mobile ? 'justify-center' : 'justify-between'
        } p-4 border-b border-white/10 flex-shrink-0`}
      >
        {(!collapsed || mobile) && (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[var(--brand-primary)] flex items-center justify-center">
              <ShieldCheck size={16} className="text-white" />
            </div>
            <span className="font-display font-bold text-sm">Admin Panel</span>
          </div>
        )}
        {!mobile && (
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
            data-testid="sidebar-toggle"
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-2 px-2">
        {navItems.map((item) => (
          <NavItem
            key={item.id}
            item={item}
            collapsed={collapsed && !mobile}
            onNavigate={onNavigate}
            anomalyCount={anomalyCount}
            openIds={openIds}
            navPathname={navPathname}
            onExpandSidebar={() => setCollapsed(false)}
          />
        ))}
      </nav>

      {/* User footer — avatar + ad + rol; tıklayınca açılan menüde Dil
          değiştir / Siteye Dön / Çıkış (bkz. dosya başındaki "NO global top
          bar" notu). Daraltılmış (icon-only) modda avatar tek başına
          trigger olur, menü davranışı DEĞİŞMEZ. */}
      <div className="p-3 border-t border-white/10 flex-shrink-0">
        <DropdownMenu open={userMenuOpen} onOpenChange={setUserMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={`w-full flex items-center gap-2 rounded-xl p-2 text-left transition-colors hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/60 ${
                collapsed && !mobile ? 'justify-center' : ''
              }`}
              aria-label={lang === 'tr' ? 'Kullanıcı menüsü' : 'User menu'}
              aria-expanded={userMenuOpen}
              data-testid="sidebar-user-menu-btn"
            >
              <div className="w-8 h-8 rounded-full bg-[var(--brand-primary)] flex items-center justify-center text-xs font-bold flex-shrink-0">
                {user?.name?.[0] || 'A'}
              </div>
              {(!collapsed || mobile) && (
                <>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold truncate">{user?.name}</p>
                    <p className="text-[10px] text-white/40 truncate">{roleLabel(user?.role, lang)}</p>
                  </div>
                  <ChevronUp
                    size={14}
                    className={`flex-shrink-0 text-white/40 transition-transform duration-200 ${userMenuOpen ? '' : 'rotate-180'}`}
                    aria-hidden="true"
                  />
                </>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="top" sideOffset={8} className="w-56">
            <div className="px-2 py-1.5">
              <p className="font-bold text-sm truncate">{user?.name}</p>
              <p className="text-xs text-[var(--text-muted)] truncate">{user?.email}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleToggleLanguage} data-testid="sidebar-lang-toggle">
              <Globe size={16} className="mr-2" />
              {lang === 'tr' ? 'English' : "Türkçe'ye geç"}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleBackToSite} data-testid="sidebar-back-to-site">
              <ExternalLink size={16} className="mr-2" />
              {lang === 'tr' ? 'Siteye Dön' : 'Back to Site'}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-red-500" data-testid="admin-logout">
              <LogOut size={16} className="mr-2" />
              {lang === 'tr' ? 'Çıkış' : 'Logout'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

// ── CmdK stub modal ───────────────────────────────────────────────────────────

function CmdKModal({ open, onClose }) {
  const { lang } = useLanguage();
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-lg">
            {lang === 'tr' ? 'Hızlı Erişim' : 'Quick Access'}
          </DialogTitle>
        </DialogHeader>
        <div className="py-6 text-center text-sm text-[var(--text-muted)]">
          {lang === 'tr'
            ? "Hızlı erişim paneli Sprint 5'te etkinleştirilecek."
            : 'Command palette will be enabled in Sprint 5.'}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Layout ───────────────────────────────────────────────────────────────

// title / titleTr props are accepted but no longer rendered in the top bar.
// Each page is responsible for its own <PageHeader> with breadcrumbs + title.
// Keeping the props in the signature avoids touching all 37 call sites.
export default function AdminLayout({ children, title, titleTr, notificationCount = 0 }) { // eslint-disable-line no-unused-vars
  const [collapsed, setCollapsed] = useState(false);
  const [cmdkOpen, setCmdkOpen] = useState(false);
  // Mobil cekmece KONTROLLU: kontrolsuz <Sheet> iken bir linke tiklamak
  // gezindiriyor ama cekmeceyi acik birakiyordu, kullanici yeni sayfayi
  // gormek icin elle kapatmak zorundaydi. Menu iki seviye derinlestikten
  // sonra bu daha da rahatsiz edici hale geldi.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { lang } = useLanguage();
  const { user } = useAuth();

  // Bildirim zili YALNIZCA admin/superadmin görür — danışman rolünde hiç
  // render edilmez (bkz. dosya başındaki "NO global top bar" notu).
  const showNotificationBell = user?.role === 'admin' || user?.role === 'superadmin';

  // Open anomaly count for sidebar badge (polling 60 s, silent on 403)
  const { data: openAnomalyCount = 0 } = useOpenAnomalyCount();

  return (
    <>
      <div className="flex h-screen bg-[var(--bg-app)] overflow-hidden" data-testid="admin-layout-v2">

        {/* Desktop Sidebar */}
        <div className="hidden lg:block flex-shrink-0">
          <SidebarContent
            collapsed={collapsed}
            setCollapsed={setCollapsed}
            lang={lang}
            onNavigate={undefined}
            anomalyCount={openAnomalyCount}
          />
        </div>

        {/* Mobile Sidebar — Sheet */}
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetTrigger asChild>
            <button
              className="lg:hidden fixed top-3 left-3 z-40 p-2 rounded-xl bg-[var(--bg-sidebar)] text-white shadow-lg"
              data-testid="mobile-sidebar-btn"
              aria-label="Open menu"
            >
              <Menu size={20} />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72 bg-[var(--bg-sidebar)] border-r-0">
            <SidebarContent
              collapsed={false}
              setCollapsed={setCollapsed}
              lang={lang}
              mobile
              onNavigate={() => setMobileNavOpen(false)}
              anomalyCount={openAnomalyCount}
            />
          </SheetContent>
        </Sheet>

        {/* Main content area */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">

          {/* Bildirim zili — sayfa başlığı (PageHeader) hizasında, sağ üstte.
              Yalnızca admin/superadmin. Panel-içi (Navbar'dan taşındı, yeniden
              yazılmadı) — bkz. components/cabin/NotificationBell.jsx. */}
          {showNotificationBell && (
            <div className="flex-shrink-0 flex justify-end px-4 lg:px-6 pt-3">
              <NotificationBell variant="desktop" />
            </div>
          )}

          {/* Page Content */}
          {/* `relative` ZORUNLU — kaldirma.
              Tailwind'in `sr-only`'si `position: absolute` kullanir. main
              konumlandirilmamissa bu etiketlerin iceren blogu BELGE olur ve
              main'in `overflow-y-auto`'su onlari KIRPMAZ: her biri statik
              konumu kadar (sayfa icinde ne kadar asagidaysa o kadar) belgeyi
              uzatir, sayfaya ikinci bir kaydirma cubugu ekler ve bosluga
              kaydirir. 25 Agustos 2026'da danisman ogrenci detayinda goruldu:
              sepete her egitim eklendiginde bir `sr-only` fiyat etiketi daha
              olusuyor ve bosluk buyuyordu (belge 855 -> 1055 px).
              `relative` iceren blogu main yapar, kirpma geri gelir. */}
          <main className="relative flex-1 overflow-y-auto p-4 lg:p-6">
            {children}
          </main>
        </div>
      </div>

      {/* Cmd+K Modal stub */}
      <CmdKModal open={cmdkOpen} onClose={() => setCmdkOpen(false)} />
    </>
  );
}
